<template>
    <div>
        <h1 class="text-3xl text-slate-800 font-semibold mb-2 leading-9">
            Setup Two Factor Authentication
        </h1>
        <div v-if="step === 3" :key="`step-a1`">
            <p class="text-slate-600 text-sm mb-4">
                Enter the phone number where you'd like to receive
                authentication codes each time you log in.
            </p>

            <div class="mb-4">
                <label
                    class="block text-slate-600 text-sm font-medium mb-2"
                    for="phone"
                >
                    Phone Number
                </label>
                <input
                    type="text"
                    id="phone"
                    v-model="registerCreds.phone"
                    class="w-full px-4 py-2.5 bg-white rounded border border-slate-200 text-slate-500 text-sm placeholder:text-slate-500 placeholder:text-sm placeholder:text-left"
                    placeholder="(###) ###-####"
                    v-maska
                    data-maska="(###) ###-####"
                    autofocus
                />
            </div>

            <label class="flex items-start gap-2 mb-6">
                <input
                    type="checkbox"
                    v-model="registerCreds.twofa_agreed"
                    value="1"
                    class="form-checkbox mt-1"
                />
                <p class="text-slate-800 text-sm">
                    I agree to receive messages from My10x at the phone number
                    provided above for the purpose of two factor authentication.
                    I understand I will receive messages anytime I login and I
                    have 2FA enabled and data rates may apply. Reply STOP to opt
                    out.
                </p>
            </label>
            <Errors :errors="errors" class="mb-6" />
            <div class="flex items-center justify-between">
                <button
                    class="px-4 py-2.5 bg-white rounded shadow border border-slate-200 text-slate-800 text-sm font-medium hover:bg-slate-50 transition-colors"
                    @click="registerStore.cancelTwoFactor()"
                >
                    Setup later
                </button>
                <button
                    class="px-4 py-2.5 bg-slate-800 rounded shadow justify-center text-white text-sm font-medium hover:bg-slate-700 transition-colors"
                    @click="registerStore.gotoStep(4)"
                >
                    Continue
                </button>
            </div>
        </div>
        <div v-if="step === 4" :key="`step-b1`">
            <p class="text-slate-800 text-sm">
                Enter the verification code sent to this phone:
            </p>
            <p class="text-slate-800 text-sm mb-4 flex items-center gap-2">
                {{ registerCreds.phone }}
                <button class="font-medium" @click="registerStore.gotoStep(3)">
                    <Icon icon="pencil" class="w-4 h-4" />
                </button>
            </p>
            <VOtpInput
                ref="otpInput"
                input-classes="otp-input"
                inputType="numeric"
                :num-inputs="6"
                v-model:value="code"
                :should-auto-focus="true"
                :should-focus-order="true"
                class="mb-4"
            />
            <p class="text-slate-800 text-sm">
                Didn't get your code?
                <button class="link" @click="resendSmsCode()">
                    Send a new one
                </button>
            </p>
            <p
                class="text-slate-800 text-sm"
                v-if="resending"
                v-html="resendCodeMessage"
            ></p>
            <Errors :errors="errors" class="mb-6 pt-4" />
            <div class="flex items-center justify-between pt-4">
                <button
                    class="px-4 py-2.5 bg-white rounded shadow border border-slate-200 text-slate-800 text-sm font-medium hover:bg-slate-50 transition-colors"
                    @click="registerStore.setShowCancel2faModal(true)"
                >
                    Setup later
                </button>
                <button
                    class="px-4 py-2.5 bg-slate-800 rounded shadow justify-center text-white text-sm font-medium hover:bg-slate-700 transition-colors"
                    @click="registerStore.validatePhoneNumber(code)"
                >
                    Verify
                </button>
            </div>
        </div>
    </div>
</template>
<script setup>
import { ref } from 'vue';
import { useRegisterStore } from '@/store/register';
import { storeToRefs } from 'pinia';
import VOtpInput from 'vue3-otp-input';
import Errors from '@/components/errors.vue';
import Icon from '@/components/ui/icon/icon.vue';
import { vMaska } from 'maska/vue';

const registerStore = useRegisterStore();
const { registerCreds, errors, resending, resendCodeMessage, step } =
    storeToRefs(registerStore);

const code = ref('');
const otpInput = ref(null);

const resendSmsCode = () => {
    registerStore.resendSmsCode().then(() => {
        otpInput.value?.clearInput();
    });
};
</script>
